import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";

// Use your existing NotFound and AppLayout components
import { ScrollToTop } from "./components/common/ScrollToTop";
import useAuthCheck from "./hooks/useAuthCheck";
import ModernLoader from "./components/common/ModernLoader";

const NotFound = lazy(() => import("./pages/OtherPage/NotFound"));
const AppLayout = lazy(() => import("./layout/AppLayout"));
const UserProfiles = lazy(() => import("./pages/UserProfiles"));
const Calendar = lazy(() => import("./pages/Calendar"));
const Blank = lazy(() => import("./pages/Blank"));
const Alerts = lazy(() => import("./pages/UiElements/Alerts"));
const Avatars = lazy(() => import("./pages/UiElements/Avatars"));
const Badges = lazy(() => import("./pages/UiElements/Badges"));
const Buttons = lazy(() => import("./pages/UiElements/Buttons"));
const Images = lazy(() => import("./pages/UiElements/Images"));
const Videos = lazy(() => import("./pages/UiElements/Videos"));
const LineChart = lazy(() => import("./pages/Charts/LineChart"));
const BarChart = lazy(() => import("./pages/Charts/BarChart"));

// Travel Desk Module
const TravelDeskDashboard = lazy(() => import("./pages/TravelDesk/TravelDeskDashboard"));
const NewTravelRequestPage = lazy(() => import("./pages/TravelDesk/NewTravelRequestPage"));
const NewHotelBookingPage = lazy(() => import("./pages/TravelDesk/NewHotelBookingPage"));
const NewVisaApplicationPage = lazy(() => import("./pages/TravelDesk/NewVisaApplicationPage"));
const TravelRequestDetailPage = lazy(() => import("./pages/TravelDesk/TravelRequestDetailPage"));

// E2P Module
const E2PDashboard = lazy(() => import("./components/e2p/E2PDashboard"));
const E2PEnquiryPage = lazy(() => import("./pages/E2P/E2PEnquiryPage"));
const E2PQuotationPage = lazy(() => import("./pages/E2P/E2PQuotationPage"));
const E2PDesignPage = lazy(() => import("./pages/E2P/E2PDesignPage"));
const E2PRequestDetailPage = lazy(() => import("./pages/E2P/E2PRequestDetailPage"));

/* -------------------------
   Forced strict auth gate
   ------------------------- */
/*
  This file hard-checks localStorage for 'auth_token'.
  - If no token -> redirects to login page immediately.
  - If token exists -> call fetchAuthUser() from auth module to validate.
  - If token is invalid/expired -> clears it and redirects to login.
*/
//TODO Test for Local, Coomment out below line for Auth
import {
  initAuthFromUrl,
  getToken,
  fetchAuthUser,
  clearToken,
} from "./api/auth";
import { LOGIN_URL } from "./config/api";

export default function App() {
  //TODO Test for Local test, Comment out below block for Auth
  const [state, setState] = useState<{
    initDone: boolean;
    isAuthenticated: boolean;
    debug: string[];
  }>({ initDone: false, isAuthenticated: false, debug: [] });

  //TODO Test for Local test, Comment out below block for Auth
  useEffect(() => {
    (async () => {
      const debug: string[] = [];
      debug.push(
        `[DEBUG] Starting strict auth gate at ${new Date().toISOString()}`,
      );

      // 1) Clean up any query token in URL and store it if present
      try {
        await initAuthFromUrl();
        debug.push("[DEBUG] initAuthFromUrl() completed");
      } catch (err) {
        debug.push("[DEBUG] initAuthFromUrl() threw: " + String(err));
      }

      // 2) Read token from localStorage
      const token = getToken();
      debug.push(
        "[DEBUG] localStorage auth_token present? -> " + (token ? "YES" : "NO"),
      );

      if (!token) {
        // No token: redirect to login page immediately
        debug.push("[DEBUG] No token found, redirecting to login...");
        console.warn("[App] No token found, redirecting to login page");
        window.location.href = LOGIN_URL;
        return;
      }

      // 3) Validate token by calling server endpoint
      try {
        const data = await fetchAuthUser();
        debug.push(
          "[DEBUG] fetchAuthUser() succeeded. server returned: " +
            JSON.stringify(data),
        );
        setState({ initDone: true, isAuthenticated: true, debug });
      } catch (err) {
        debug.push("[DEBUG] fetchAuthUser() failed: " + String(err));
        // clear invalid token so subsequent reloads are clean
        try {
          clearToken();
          debug.push("[DEBUG] clearToken() executed");
        } catch {}
        // Redirect to login on invalid/expired token
        debug.push("[DEBUG] Invalid token, redirecting to login...");
        console.warn(
          "[App] Token validation failed, redirecting to login page",
        );
        window.location.href = LOGIN_URL;
      }
    })();
  }, []);

  // While initializing, we skip the 1st loader to keep only the 2nd (Dashboard) one
  if (!state.initDone) {
    return null;
  }

  // If not authenticated -> show NotFound and debug panel (guaranteed)
  //TODO Test for Local test, Comment out below block for Auth
  if (!state.isAuthenticated) {
    return (
      <>
        <ToastContainer position="top-right" autoClose={2000} />
        <Router>
          <Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
            <NotFound
              authRequiredMessage="Auth required: A valid token was not found. Please sign in again."
              debugLines={
                import.meta.env.MODE === "development" ? state.debug : undefined
              }
              onClearToken={() => {
                localStorage.removeItem("auth_token");
                location.reload();
              }}
            />
          </Suspense>
        </Router>
      </>
    );
  }

  // Authenticated: render the real app
  return <AuthenticatedApp />;
}

// Separate component for authenticated users with token expiration check
function AuthenticatedApp() {
  // NOW check token expiration only after authentication is confirmed
  useAuthCheck();

  return (
    <>
      <ToastContainer position="top-right" autoClose={2000} />
      <Router>
        <ScrollToTop />
        <Suspense
          fallback={
            <div
              style={{ display: "grid", placeItems: "center", height: "60vh" }}
            >
              Loading…
            </div>
          }
        >
          <Routes>
            <Route element={<AppLayout />}>
              <Route index path="/" element={<E2PDashboard />} />

              {/* E2P Routes */}
              <Route path="/e2p" element={<E2PDashboard />} />
              <Route path="/e2p/new-enquiry" element={<E2PEnquiryPage />} />
              <Route path="/e2p/new-quotation" element={<E2PQuotationPage />} />
              <Route path="/e2p/new-design" element={<E2PDesignPage />} />
              <Route path="/e2p/view/:type/:requestId" element={<E2PRequestDetailPage />} />
              <Route path="/e2p/edit/:type/:requestId" element={<E2PRequestDetailPage />} />
              
              {/* Travel Desk Routes */}
              <Route path="/travel-desk" element={<TravelDeskDashboard />} />
              <Route path="/travel-desk/new-travel" element={<NewTravelRequestPage />} />
              <Route path="/travel-desk/new-hotel" element={<NewHotelBookingPage />} />
              <Route path="/travel-desk/new-visa" element={<NewVisaApplicationPage />} />
              {/* View/Edit routes - using tabbed detail view */}
              <Route path="/travel-desk/view/:type/:requestId" element={<TravelRequestDetailPage />} />
              <Route path="/travel-desk/edit/:type/:requestId" element={<TravelRequestDetailPage />} />

              <Route path="/profile" element={<UserProfiles />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/blank" element={<Blank />} />
              <Route path="/alerts" element={<Alerts />} />
              <Route path="/avatars" element={<Avatars />} />
              <Route path="/badge" element={<Badges />} />
              <Route path="/buttons" element={<Buttons />} />
              <Route path="/images" element={<Images />} />
              <Route path="/videos" element={<Videos />} />
              <Route path="/line-chart" element={<LineChart />} />
              <Route path="/bar-chart" element={<BarChart />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
    </>
  );
}
